import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SharedCollection = () => {
  const { shareToken } = useParams();
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [addingEntry, setAddingEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({ content: "", annotation: "" });

  const fetchShared = async () => {
    try {
      const res = await axios.get(`${API}/shared/${shareToken}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.status === 404 ? "This shared link is invalid or has expired." : "Failed to load shared volume.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchShared(); }, [shareToken]);

  const handleSaveToMyRyan = async () => {
    if (!user) {
      login();
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/shared/${shareToken}/save`);
      setSaved(true);
      toast.success(`"${res.data.name}" saved with ${res.data.entries_copied} entries`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save volume");
    } finally {
      setSaving(false);
    }
  };

  const handleAddEntry = async () => {
    if (!user) { login(); return; }
    if (!newEntry.content.trim()) { toast.error("Entry content is required"); return; }
    try {
      await axios.post(`${API}/shared/${shareToken}/entries`, {
        message_content: newEntry.content,
        annotation: newEntry.annotation || null,
        source: "dashboard",
        source_url: window.location.href,
      });
      toast.success("Entry added to shared volume");
      setNewEntry({ content: "", annotation: "" });
      setAddingEntry(false);
      fetchShared(); // Refresh
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add entry");
    }
  };

  const isCollaboratorVolume = data?.share_level === "shared_collaborators";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0600] flex items-center justify-center">
        <div className="spinner-ember" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0600] flex flex-col items-center justify-center text-center px-4">
        <div className="text-4xl mb-4 text-[#4A3A1A]">x</div>
        <h1 className="font-serif text-xl font-bold text-[#FDF8EE] mb-2">{error}</h1>
        <Link to="/" className="font-mono text-[10px] text-[#E8A030] hover:underline uppercase tracking-[1px] mt-4">
          Go to Ryan
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0600]" data-testid="shared-collection-page">
      {/* Header */}
      <header className="border-b border-[rgba(232,160,48,0.1)] bg-[#1C1004]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-[9px] h-[9px] bg-[#E8A030] rotate-45" />
            <span className="font-serif font-black text-lg text-[#FDF8EE]">Ryan</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px]">
              {data?.share_level === "shared_collaborators" ? "Collaborative Volume" : "Shared Volume"}
            </span>
            {isCollaboratorVolume && user && (
              <button
                onClick={() => setAddingEntry(!addingEntry)}
                className="btn-ghost text-xs py-1.5 px-3"
                data-testid="add-entry-toggle-btn"
              >
                {addingEntry ? "Cancel" : "+ Add Entry"}
              </button>
            )}
            {!saved ? (
              <button
                onClick={handleSaveToMyRyan}
                disabled={saving}
                className="btn-primary text-xs py-1.5 px-4"
                data-testid="save-to-my-ryan-btn"
              >
                {saving ? "Saving..." : user ? "Save to my Ryan" : "Sign in to Save"}
              </button>
            ) : (
              <span className="font-mono text-[9px] text-[#7AB8A0] uppercase tracking-[1px]">Saved</span>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span style={{ color: data.color }} className="text-lg">&#9670;</span>
            <h1 className="font-serif text-2xl font-bold text-[#FDF8EE]" data-testid="shared-collection-name">
              {data.collection_name}
            </h1>
            {isCollaboratorVolume && (
              <span className="font-mono text-[7px] text-[#A08CC8] bg-[rgba(160,140,200,0.12)] px-1.5 py-0.5 uppercase tracking-[1px]">Collaborative</span>
            )}
          </div>
          {data.description && (
            <p className="text-sm text-[#8C7A5A] mb-2">{data.description}</p>
          )}
          <p className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px]">
            Shared by {data.shared_by} &middot; {data.bookmark_count} entr{data.bookmark_count !== 1 ? "ies" : "y"}
          </p>
        </div>

        {/* Add Entry Form (collaborators only) */}
        {addingEntry && (
          <div className="mb-6 bg-[#1C1004] border border-[rgba(160,140,200,0.2)] p-5" data-testid="add-entry-form">
            <h3 className="font-serif font-bold text-sm text-[#FDF8EE] mb-3 flex items-center gap-2">
              <span className="text-[#A08CC8]">+</span> Add Entry to this Volume
            </h3>
            <textarea
              value={newEntry.content}
              onChange={e => setNewEntry(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Paste or type the AI exchange you want to save..."
              className="input-dark w-full min-h-[100px] mb-3 text-sm"
              data-testid="new-entry-content"
            />
            <input
              value={newEntry.annotation}
              onChange={e => setNewEntry(prev => ({ ...prev, annotation: e.target.value }))}
              placeholder="Your annotation (optional)"
              className="input-dark w-full mb-3 text-sm"
              data-testid="new-entry-annotation"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setAddingEntry(false)} className="btn-ghost text-xs py-1.5 px-4">Cancel</button>
              <button onClick={handleAddEntry} className="btn-primary text-xs py-1.5 px-4" data-testid="submit-entry-btn">Add Entry</button>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {data.bookmarks.map((bm, i) => (
            <div
              key={bm.bookmark_id}
              className="card-coke p-5 border-l-[3px]"
              style={{ borderLeftColor: data.color }}
              data-testid={`shared-bookmark-${i}`}
            >
              {bm.annotation && (
                <div className="bg-[rgba(200,96,10,0.1)] border-l-2 border-[#C8600A] px-3 py-2 mb-3">
                  <p className="text-sm text-[#F5ECD0] font-medium">{bm.annotation}</p>
                </div>
              )}
              {bm.images?.length > 0 && (
                <div className="flex gap-2 mb-3 flex-wrap">
                  {bm.images.map((img) => (
                    <img
                      key={img.image_id}
                      src={`${API}/files/${img.storage_path}?share=${shareToken}`}
                      alt={img.original_filename}
                      className="h-24 w-auto object-cover border border-[rgba(232,160,48,0.1)]"
                    />
                  ))}
                </div>
              )}
              <p className="text-[13px] text-[#8C7A5A] leading-[1.7] whitespace-pre-wrap">
                {bm.message_content}
              </p>
              <div className="flex items-center justify-between mt-3 font-mono text-[9px]">
                <span className={`tag ${bm.message_role === "assistant" ? "tag-pro" : ""}`}>
                  {bm.message_role}
                </span>
                <span className="text-[#4A3A1A]">
                  {bm.created_at ? new Date(bm.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center border-t border-[rgba(232,160,48,0.1)] pt-8">
          <p className="text-sm text-[#8C7A5A] mb-4">
            {user ? "Save this volume to your Ryan and keep building your own AI encyclopedia." : "Save your own AI conversations with Ryan."}
          </p>
          {!user ? (
            <button onClick={login} className="btn-primary" data-testid="shared-cta">
              Get Started Free
            </button>
          ) : !saved ? (
            <button onClick={handleSaveToMyRyan} disabled={saving} className="btn-primary" data-testid="shared-cta-save">
              {saving ? "Saving..." : "Save to my Ryan →"}
            </button>
          ) : (
            <Link to="/dashboard" className="btn-primary inline-block" data-testid="shared-cta-dashboard">
              Go to Dashboard →
            </Link>
          )}
        </div>
      </main>
    </div>
  );
};

export default SharedCollection;
